import { Box, Button, Card, Grid, Group, Paper, Progress, Stack, Text, Title, Badge, Tabs, Divider } from '@mantine/core';
import { IconCircleCheck, IconCircleDashed, IconClipboard, IconClipboardCheck, IconClipboardHeart, IconFileDescription, IconListCheck, IconShieldLock, IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Document, QuestionnaireForm, useMedplum } from '@medplum/react';
import { useMedplumProfile } from '../../components/documents/IntakeSummary';
import { useState, useEffect, type JSX } from 'react';
import { DocumentService } from '../../services/DocumentService';
import { DocumentStatus } from '../../types/documents';
import { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useDocumentsContext } from '../../contexts/DocumentsContext';
import { ConsentForm } from './ConsentForm';

// Component to display an embedded form
function EmbeddedForm({ formId }: { formId: string }): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { refreshDocuments } = useDocumentsContext();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | null>(null);
  const [documentInfo, setDocumentInfo] = useState<DocumentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  useEffect(() => {
    async function loadForm() {
      try {
        setIsLoading(true);
        
        // First, get the form info from our document list
        let formInfo: DocumentStatus | undefined;
        if (formId) {
          // Get the patient profile - we need to cast it as any since the patient type can vary
          const patientProfile = medplum.getProfile();
          if (patientProfile && 'resourceType' in patientProfile) {
            // Cast to any to get around type checking - the DocumentService handles different profile types
            formInfo = (await DocumentService.getAssignedForms(medplum, patientProfile as any))
              .find(doc => doc.id === formId);
            
            if (formInfo) {
              setDocumentInfo(formInfo);
            }
          }
        }
        
        // Now load the actual form data
        const { response, questionnaire: formTemplate } = await DocumentService.getFormById(medplum, formId);
        
        setQuestionnaireResponse(response);
        
        if (formTemplate && formTemplate.resourceType === 'Questionnaire') {
          setQuestionnaire(formTemplate as Questionnaire);
        } else if (formInfo && formInfo.category === 'consent') {
          // For consent forms, we'll redirect to the specialized consent form page
          // Just show a message instead of error
          setError(null);
        } else {
          setError('Form template not found or not a Questionnaire.');
        }
        
        // Check if form is already completed
        if (response && response.status === 'completed') {
          setIsSubmitted(true);
        }
      } catch (err) {
        console.error('Error loading embedded form:', err);
        setError('Error loading form.');
      } finally {
        setIsLoading(false);
      }
    }
    
    if (formId) {
      loadForm();
    }
  }, [medplum, formId]);
  
  async function handleSubmit(formData: QuestionnaireResponse): Promise<void> {
    try {
      // If we have an existing QuestionnaireResponse, update it
      if (questionnaireResponse && questionnaireResponse.id) {
        // Update with submitted data while preserving extensions
        const updatedResponse = {
          ...questionnaireResponse,
          status: 'completed' as const,
          item: formData.item,
        };
        
        await medplum.updateResource(updatedResponse);
      } else {
        // Submit new response
        await medplum.createResource(formData);
      }
      
      setIsSubmitted(true);
      // Immediately refresh the documents list
      refreshDocuments();
    } catch (err) {
      console.error('Error submitting embedded form:', err);
      setError('Error submitting form. Please try again.');
    }
  }
  
  // If this is a consent form, render a redirect button instead of an error
  if (documentInfo && documentInfo.category === 'consent' && !isSubmitted) {
    return (
      <Box p="xl">
        <Paper withBorder p="lg" radius="md" shadow="sm">
          <Stack align="center" gap="lg">
            <IconShieldLock size={48} color="var(--mantine-color-indigo-6)" />
            <Title order={4}>Consent Form</Title>
            <Text ta="center">
              This consent form requires special handling and must be completed on its dedicated page.
            </Text>
            <Button 
              color="indigo" 
              size="md"
              leftSection={<IconArrowRight size={16} />}
              onClick={() => navigate(documentInfo.href)}
            >
              Open Consent Form
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }
  
  // Similar for assessment forms that need special handling
  if (documentInfo && documentInfo.category === 'assessment' && !questionnaire && !isSubmitted) {
    return (
      <Box p="xl">
        <Paper withBorder p="lg" radius="md" shadow="sm">
          <Stack align="center" gap="lg">
            <IconClipboardHeart size={48} color="var(--mantine-color-blue-6)" />
            <Title order={4}>Assessment Form</Title>
            <Text ta="center">
              This assessment needs to be completed on its dedicated page.
            </Text>
            <Button 
              color="blue" 
              size="md"
              leftSection={<IconArrowRight size={16} />}
              onClick={() => navigate(documentInfo.href)}
            >
              Open Assessment
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }
  
  if (isLoading) {
    return <Box p="md"><Text>Loading form...</Text></Box>;
  }
  
  if (error) {
    return (
      <Box p="md">
        <Text color="red">{error}</Text>
      </Box>
    );
  }
  
  if (isSubmitted) {
    return (
      <Box p="md">
        <Paper withBorder p="md" radius="md">
          <Stack align="center">
            <IconCircleCheck size={32} color="var(--mantine-color-teal-6)" />
            <Text fw={500}>Form submitted successfully!</Text>
            <Text>Thank you for completing this form.</Text>
          </Stack>
        </Paper>
      </Box>
    );
  }
  
  if (!questionnaire) {
    return (
      <Box p="md">
        <Text>No form available. Please select a different form from the tabs above.</Text>
      </Box>
    );
  }
  
  return (
    <Box p="md">
      <QuestionnaireForm 
        questionnaire={questionnaire} 
        onSubmit={handleSubmit}
      />
    </Box>
  );
}

export function DocumentsIndex(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { profile } = useMedplumProfile();
  const { refreshKey, refreshDocuments } = useDocumentsContext();
  const [documents, setDocuments] = useState<DocumentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  
  // Format first name if possible
  const firstName = profile.name?.[0]?.given?.[0] || 'there';

  // Load documents to calculate progress
  useEffect(() => {
    async function loadDocuments() {
      try {
        setLoading(true);
        const assignedForms = await DocumentService.getAssignedForms(medplum, profile);
        setDocuments(assignedForms);
      } catch (err) {
        console.error('Error loading forms:', err);
      } finally {
        setLoading(false);
      }
    }
    
    loadDocuments();
  }, [medplum, profile, refreshKey]);
  
  // Group documents by type
  const intakeForms = documents.filter(doc => doc.category === 'questionnaire');
  const consentForms = documents.filter(doc => doc.category === 'consent');
  const assessmentForms = documents.filter(doc => doc.category === 'assessment');
  
  // Calculate completion percentages
  const calculateCompletion = (docs: DocumentStatus[]) => {
    if (!docs.length) return 0;
    const completed = docs.filter(doc => doc.status === 'completed').length;
    return Math.round((completed / docs.length) * 100);
  };
  
  // Get next incomplete form in each category
  const getNextForm = (docs: DocumentStatus[]): DocumentStatus | undefined => {
    return docs.find(doc => doc.status !== 'completed');
  };
  
  const intakeProgress = calculateCompletion(intakeForms);
  const consentProgress = calculateCompletion(consentForms);
  const assessmentProgress = calculateCompletion(assessmentForms);
  
  const nextIntakeForm = getNextForm(intakeForms);
  const nextConsentForm = getNextForm(consentForms);
  const nextAssessmentForm = getNextForm(assessmentForms);
  
  // Set the first incomplete form as active if none is selected - MOVED HERE
  useEffect(() => {
    if (!activeFormId && !loading) {
      if (nextIntakeForm) {
        setActiveFormId(nextIntakeForm.id);
      } else if (nextConsentForm) {
        setActiveFormId(nextConsentForm.id);
      } else if (nextAssessmentForm) {
        setActiveFormId(nextAssessmentForm.id);
      }
    }
  }, [loading, nextIntakeForm, nextConsentForm, nextAssessmentForm, activeFormId]);
  
  // Calculate overall progress
  const totalForms = documents.length;
  const completedForms = documents.filter(doc => doc.status === 'completed').length;
  const overallProgress = totalForms ? Math.round((completedForms / totalForms) * 100) : 0;
  
  return (
    <Box p="md">
      <Paper withBorder p="xl" radius="md" shadow="sm">
        <Stack gap="xl">
          <Title order={3}>Welcome, {firstName}</Title>
          
          <Text maw={700}>
            Our client portal provides access to your health information and helps streamline your care. 
            Please complete any assigned intake forms and questionnaires before your first appointment.
          </Text>
          
          {/* Overall Progress Section */}
          <Paper withBorder p="lg" radius="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>Your Progress</Title>
                <Text fw={500} c={overallProgress === 100 ? "teal" : "inherit"}>
                  {completedForms} of {totalForms} forms completed
                </Text>
              </Group>
              
              <Progress 
                value={overallProgress} 
                size="xl" 
                radius="xl"
                color={overallProgress === 100 ? "teal" : overallProgress > 50 ? "blue" : "indigo"}
                striped={false}
                animated={false}
              />
              {overallProgress === 100 ? (
                <Group>
                  <IconCircleCheck size={20} color="var(--mantine-color-teal-6)" />
                  <Text c="teal">All forms completed! Thank you for your time.</Text>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  Complete all required forms before your first appointment to help us provide the best care possible.
                </Text>
              )}
            </Stack>
          </Paper>
          
          {/* Form Categories with Embedded Form */}
          {loading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Text>Loading forms...</Text>
            </Box>
          ) : overallProgress === 100 ? (
            <Paper p="xl" withBorder radius="md" shadow="sm" bg="var(--mantine-color-teal-0)">
              <Stack align="center" gap="md">
                <IconCircleCheck size={48} color="var(--mantine-color-teal-6)" />
                <Title order={3}>All Forms Completed</Title>
                <Text ta="center" maw={600}>
                  Thank you for completing all your assigned forms. This information will help your 
                  healthcare provider deliver personalized care tailored to your needs.
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Box>
              <Tabs 
                defaultValue="questionnaire" 
                value={
                  activeFormId ? 
                    (documents.find(d => d.id === activeFormId)?.category || "questionnaire") : 
                    (nextIntakeForm ? "questionnaire" : 
                     nextConsentForm ? "consent" : 
                     nextAssessmentForm ? "assessment" : "questionnaire")
                }
                onChange={(value: string | null) => {
                  // Handle tab change - find the appropriate next form for the selected category
                  if (!value) return;
                  
                  const category = value as 'questionnaire' | 'consent' | 'assessment';
                  let nextForm;
                  
                  if (category === 'questionnaire') {
                    nextForm = nextIntakeForm;
                  } else if (category === 'consent') {
                    nextForm = nextConsentForm;
                  } else if (category === 'assessment') {
                    nextForm = nextAssessmentForm;
                  }
                  
                  if (nextForm) {
                    setActiveFormId(nextForm.id);
                  }
                }}
              >
                <Tabs.List grow>
                  <Tabs.Tab 
                    value="questionnaire" 
                    leftSection={<IconClipboard size={16} />}
                    disabled={intakeForms.length === 0}
                    rightSection={
                      <Badge 
                        color={intakeProgress === 100 ? "teal" : "blue"} 
                        variant="light"
                        size="sm"
                      >
                        {intakeForms.filter(f => f.status === 'completed').length}/{intakeForms.length}
                      </Badge>
                    }
                  >
                    Intake Forms
                  </Tabs.Tab>
                  
                  <Tabs.Tab 
                    value="consent" 
                    leftSection={<IconShieldLock size={16} />}
                    disabled={consentForms.length === 0}
                    rightSection={
                      <Badge 
                        color={consentProgress === 100 ? "teal" : "indigo"} 
                        variant="light"
                        size="sm"
                      >
                        {consentForms.filter(f => f.status === 'completed').length}/{consentForms.length}
                      </Badge>
                    }
                  >
                    Consent Forms
                  </Tabs.Tab>
                  
                  <Tabs.Tab 
                    value="assessment" 
                    leftSection={<IconClipboardHeart size={16} />}
                    disabled={assessmentForms.length === 0}
                    rightSection={
                      <Badge 
                        color={assessmentProgress === 100 ? "teal" : "blue"} 
                        variant="light"
                        size="sm"
                      >
                        {assessmentForms.filter(f => f.status === 'completed').length}/{assessmentForms.length}
                      </Badge>
                    }
                  >
                    Assessments
                  </Tabs.Tab>
                </Tabs.List>
                
                <Box mt="md">
                  {/* Display the actual form content */}
                  {activeFormId ? (
                    /* Check if this is a consent form and use the specialized component */
                    (documents.find(d => d.id === activeFormId)?.category === 'consent' ? (<Document width={800}>
                      <ConsentForm 
                        formId={activeFormId} 
                        embedded={true} 
                        onComplete={() => refreshDocuments()} 
                      />
                    </Document>) : (<Document width={800}>
                      <EmbeddedForm formId={activeFormId} />
                    </Document>))
                  ) : (
                    <Paper p="xl" withBorder>
                      <Stack align="center">
                        <Text fw={500}>No forms available in this category</Text>
                      </Stack>
                    </Paper>
                  )}
                </Box>
              </Tabs>
              
              {/* Form Selection (when there are multiple incomplete forms) */}
              {activeFormId && (
                <Paper withBorder p="md" radius="md" mt="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Title order={5}>Other Forms</Title>
                      <Badge>{documents.filter(d => d.status !== 'completed').length.toString()} remaining</Badge>
                    </Group>
                    <Divider />
                    <Stack gap="xs">
                      {documents
                        .filter(form => form.status !== 'completed' && form.id !== activeFormId)
                        .map(form => (
                          <Group key={form.id} justify="space-between">
                            <Text size="sm">{form.name}</Text>
                            <Button 
                              size="xs" 
                              variant="subtle"
                              onClick={() => setActiveFormId(form.id)}
                            >
                              Switch to this form
                            </Button>
                          </Group>
                        ))}
                      {documents.filter(form => form.status !== 'completed' && form.id !== activeFormId).length === 0 && (
                        <Text size="sm" c="dimmed" ta="center">No other incomplete forms</Text>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </Box>
          )}
          
          <Paper p="md" withBorder radius="sm" bg="var(--mantine-color-gray-0)" mt="xl">
            <Group align="flex-start" gap="lg">
              <IconListCheck size={24} color="var(--mantine-color-gray-7)" />
              <Stack gap="xs">
                <Title order={5}>Completing Your Forms</Title>
                <Text size="sm">
                  Please complete all required forms at your earliest convenience. Your responses 
                  are securely saved as you progress, allowing you to return and complete them later if needed.
                  If you encounter any difficulties or have questions about a specific form, please contact our 
                  office for assistance.
                </Text>
              </Stack>
            </Group>
          </Paper>
        </Stack>
      </Paper>
    </Box>
  );
}